// Third-party packages
import {
	Collection,
} from '@@types/Collection';
import {
	createSlice,
	PayloadAction,
} from '@reduxjs/toolkit';

// Templates / Layouts / Pages
import {
	LotsStateProps,
} from '@layouts/Pages/PageOfferEntry';

export interface OfferEntryLotsState {
	lots: Collection<LotsStateProps, LotsStateProps>;
	lots_total: {
		total_area: number;
		total_area_main_natures: number;
	};
	isError: boolean;
	isLoading: boolean;
}

export interface OffersEntryLotsStore {
	[offerId: number]: OfferEntryLotsState;
}

const initialState: OffersEntryLotsStore = {};

export const offersEntryLotsSlice = createSlice({
	initialState,
	name: 'offers_entry_lots',
	reducers: {
		addOfferEntryLot: (state, action: PayloadAction<{
			lot: LotsStateProps;
			offerId: number;
		}>) => {
			const {
				lot,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					isError: false,
					isLoading: false,
					lots: {
						collection: [],
						count: 0,
					} as unknown as Collection<LotsStateProps, LotsStateProps>,
					lots_total: {
						total_area: 0,
						total_area_main_natures: 0,
					},
				};
			}
			// Upsert by id if present, else by rowId if present, else append
			const collection = state[offerId].lots.collection;
			let isUpdated = false;
			if (lot?.id !== undefined && lot?.id !== null) {
				for (let i = 0; i < collection.length; i++) {
					if (collection[i]?.id === lot.id) {
						collection[i] = {
							...collection[i],
							...lot,
						};
						isUpdated = true;
						break;
					}
				}
			} else if (typeof (lot as any)?.rowId === 'number') {
				for (let i = 0; i < collection.length; i++) {
					if ((collection[i] as any)?.rowId === (lot as any).rowId && (collection[i]?.id === undefined || collection[i]?.id === null)) {
						collection[i] = {
							...collection[i],
							...lot,
						};
						isUpdated = true;
						break;
					}
				}
			}
			if (!isUpdated) {
				collection.push(lot);
			}
			state[offerId].lots.count = state[offerId].lots.collection.length;
		},
		clearOfferEntryLots: (state, action: PayloadAction<number>) => {
			const offerId = action.payload;
			delete state[offerId];
		},
		deleteOfferEntryLots: (state, action: PayloadAction<{
			lotIds: number[];
			offerId: number;
		}>) => {
			const {
				lotIds,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				return;
			}
			// Remove lots from the collection
			state[offerId].lots.collection = state[offerId].lots.collection.filter(
				(lot) => !lotIds.includes(lot.id as number),
			);
			state[offerId].lots.count = state[offerId].lots.collection.length;
		},
		setOfferEntryLots: (state, action: PayloadAction<{
			lots: Collection<LotsStateProps, LotsStateProps>;
			offerId: number;
		}>) => {
			const {
				lots,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					isError: false,
					isLoading: false,
					lots: {
						collection: [],
						count: 0,
					} as unknown as Collection<LotsStateProps, LotsStateProps>,
					lots_total: {
						total_area: 0,
						total_area_main_natures: 0,
					},
				};
			}
			// Merge incoming server lots with existing drafts (no id) to preserve local edits
			const existingDrafts = (state[offerId].lots.collection || []).filter((l) => l?.id === undefined || l?.id === null);
			const mergedCollection = [
				...(lots?.collection || []),
				...existingDrafts.filter((draft) => {
					// Keep only drafts that don't duplicate an incoming server lot by id (shouldn't) and are unique by rowId
					const draftRowId = (draft as any)?.rowId;
					return typeof draftRowId === 'number'
						? !(lots?.collection || []).some((srv) => (srv as any)?.rowId === draftRowId)
						: true;
				}),
			];
			state[offerId].lots = {
				...lots,
				collection: mergedCollection,
				count: mergedCollection.length,
			} as unknown as Collection<LotsStateProps, LotsStateProps>;
		},
		setOfferEntryLotsError: (state, action: PayloadAction<{
			isError: boolean;
			offerId: number;
		}>) => {
			const {
				isError,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					isError: false,
					isLoading: false,
					lots: {
						collection: [],
						count: 0,
					} as unknown as Collection<LotsStateProps, LotsStateProps>,
					lots_total: {
						total_area: 0,
						total_area_main_natures: 0,
					},
				};
			}
			state[offerId].isError = isError;
		},
		setOfferEntryLotsLoading: (state, action: PayloadAction<{
			isLoading: boolean;
			offerId: number;
		}>) => {
			const {
				isLoading,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					isError: false,
					isLoading: false,
					lots: {
						collection: [],
						count: 0,
					} as unknown as Collection<LotsStateProps, LotsStateProps>,
					lots_total: {
						total_area: 0,
						total_area_main_natures: 0,
					},
				};
			}
			state[offerId].isLoading = isLoading;
		},
		setOfferEntryLotsTotal: (state, action: PayloadAction<{
			lots_total: {
				total_area: number;
				total_area_main_natures: number;
			};
			offerId: number;
		}>) => {
			const {
				lots_total,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					isError: false,
					isLoading: false,
					lots: {
						collection: [],
						count: 0,
					} as unknown as Collection<LotsStateProps, LotsStateProps>,
					lots_total: {
						total_area: 0,
						total_area_main_natures: 0,
					},
				};
			}
			state[offerId].lots_total = lots_total;
		},
		updateOfferEntryLot: (state, action: PayloadAction<{
			lot: LotsStateProps;
			offerId: number;
		}>) => {
			const {
				lot,
				offerId,
			} = action.payload;
			if (!state[offerId]) {
				state[offerId] = {
					isError: false,
					isLoading: false,
					lots: {
						collection: [],
						count: 0,
					} as unknown as Collection<LotsStateProps, LotsStateProps>,
					lots_total: {
						total_area: 0,
						total_area_main_natures: 0,
					},
				};
			}
			// Upsert by id if present; otherwise by rowId
			const collection = state[offerId].lots.collection || [];
			let foundIndex = -1;
			if (lot?.id !== undefined && lot?.id !== null) {
				foundIndex = collection.findIndex((existingLot) => existingLot?.id === lot.id);
			}
			if (foundIndex === -1 && typeof (lot as any)?.rowId === 'number') {
				const rowId = (lot as any).rowId as number;
				foundIndex = collection.findIndex((existingLot) => (existingLot as any)?.rowId === rowId && (existingLot?.id === undefined || existingLot?.id === null));
			}
			if (foundIndex !== -1) {
				collection[foundIndex] = {
					...collection[foundIndex],
					...lot,
				};
			} else {
				collection.push(lot);
			}
			state[offerId].lots.collection = collection;
			state[offerId].lots.count = collection.length;
		},
	},
});

export const {
	addOfferEntryLot,
	clearOfferEntryLots,
	deleteOfferEntryLots,
	setOfferEntryLots,
	setOfferEntryLotsError,
	setOfferEntryLotsLoading,
	setOfferEntryLotsTotal,
	updateOfferEntryLot,
} = offersEntryLotsSlice.actions;

export const offersEntryLotsReducer = offersEntryLotsSlice.reducer;
